import { useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { adminQuotingSmartCensusParseJobs } from 'rails_routes'
import { Banner, FileUpload } from 'slab'
import { postQuotingSmartCensusParseJobs } from 'utils/web_api'

import SmartCensusParseJobDetails from './SmartCensusParseJobDetails'

export default function SmartCensusUploader () {
  const [error, setError] = useState<string>()
  const [isNavigating, setIsNavigating] = useState(false)
  const [jobId, setJobId] = useState<string | null>(null)

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      // Read jobId from state - will be null/undefined when navigating back to "new" page
      const stateJobId = event.state?.jobId ?? null
      setJobId(stateJobId)
      setIsNavigating(false)
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  const uploadFileMutation = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData()
      formData.append('file', file)

      return postQuotingSmartCensusParseJobs({ body: formData })
    },
    onSuccess: (job) => {
      setIsNavigating(true)
      setJobId(job.id)
      window.history.pushState({ jobId: job.id }, '', adminQuotingSmartCensusParseJobs.show({ id: job.id }))
    },
    onError: (error) => {
      setError(error.message)
    },
  })

  const uploadFile = (file: File | undefined) => {
    if (!file) return
    if (file.type !== 'text/csv') {
      setError(`Please convert your file to a CSV, provided file was "${file.type}"`)
      return
    }
    setError(undefined)
    uploadFileMutation.mutate(file)
  }

  if (jobId) {
    return <SmartCensusParseJobDetails jobId={jobId} />
  }

  return (
    <div>
      {error && (
        <div className='mb-4'>
          <Banner title='Failed to upload file' type='critical'>
            <p>{error}</p>
          </Banner>
        </div>
      )}
      <FileUpload
        accept='text/csv'
        onChange={(e) => uploadFile(e.target.files?.[0])}
        disabled={uploadFileMutation.isPending || isNavigating}
      >
        {({ dropping }) => (
          <div className='min-h-64 flex items-center justify-center text-center text-sm text-gray-500'>
            {uploadFileMutation.isPending || isNavigating
              ? 'Uploading...'
              : dropping
                ? 'Drop to upload'
                : 'Upload your census file to be parsed'}
          </div>
        )}
      </FileUpload>
    </div>
  )
}
